/**
 * Layout.jsx - Main admin dashboard layout wrapper
 * 
 * ADMIN THEME ARCHITECTURE
 * ========================
 * This layout applies the 'admin-theme' class which enables scoped CSS variables
 * for a modern SaaS admin interface. The admin theme uses:
 * 
 * - Clean Inter/Lexend typography (NO pixel fonts in admin UI)
 * - GCH palette: slate bg, gold accent (#F4B942), coral CTA (#E56A54)
 * - Gold focus rings and selected states
 * 
 * RETRO STYLING RULES
 * ===================
 * Retro/arcade styling is NOT allowed in:
 * - Navigation, sidebar, header components
 * - Form labels, inputs, textareas, selects
 * - Tables, data displays, analytics
 * - Settings panels, modals (except game preview modals)
 * 
 * Retro styling IS allowed in:
 * - StyleLibraryPicker (theme library)
 * - Game preview iframes (player-facing content)
 * - Template gallery card preview images
 * - Small "Arcade" badge decorations (very subtle)
 * 
 * See input.css for the complete admin-theme CSS variable definitions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationTriangleIcon, CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';

import Sidebar from './Sidebar';
import Header from './Header';
import LockedOutScreen from './components/LockedOutScreen';
import { useUser } from './userContext';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';

/* ── Global notification toast (GCH-styled, replaces native alert()) ── */
function AppNotification() {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((e) => {
    const { type, message } = e.detail;
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  useEffect(() => {
    window.addEventListener('app-notify', addNotification);
    return () => window.removeEventListener('app-notify', addNotification);
  }, [addNotification]);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] flex flex-col gap-2 pointer-events-none" style={{ fontFamily: '"Inter", sans-serif' }}>
      <AnimatePresence>
        {notifications.map(n => (
          <motion.div
            key={n.id}
            initial={{ opacity: 0, y: -12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="pointer-events-auto gch-bg-card border border-[var(--admin-border)] rounded-xl shadow-xl px-4 py-3 flex items-center gap-3 min-w-[280px] max-w-md"
          >
            {n.type === 'success' ? (
              <CheckCircleIcon className="w-5 h-5 text-green-400 shrink-0" />
            ) : (
              <ExclamationCircleIcon className="w-5 h-5 text-red-400 shrink-0" />
            )}
            <p className="text-sm font-medium gch-text-primary flex-1">{n.message}</p>
            <button
              type="button"
              onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}
              className="gch-text-muted hover:gch-text-primary transition-colors shrink-0"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    businessProfile,
    userProfile,
    switchActiveClient,
    isLockedOut,
    campaigns,
  } = useUser();
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Listen for maintenance mode (kill switch)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'killSwitch'), (snap) => {
      setMaintenanceMode(snap.exists() ? (snap.data().enabled || false) : false);
    }, () => setMaintenanceMode(false));
    return unsub;
  }, []);


  // Load Inter, Lexend, and Bungee (arcade headings)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=Lexend:wght@400;500;600;700;900&family=Bungee&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const studioStyle = {
    fontFamily: '"Inter", sans-serif',
  };

  // Add class to html so XL font-size scale applies only in admin (rem is root-relative)
  useEffect(() => {
    document.documentElement.classList.add('admin-view');
    return () => document.documentElement.classList.remove('admin-view');
  }, []);

  // Maintenance mode: block non-admins, admins can still access
  const isAdmin = userProfile?.role === 'admin';
  if (maintenanceMode && !isAdmin) {
    return <LockedOutScreen maintenance />;
  }

  if (isLockedOut) {
    return <LockedOutScreen />;
  }

  // Multi-workspace retired with subscriptions. Escape hatch fires when the
  // current businessProfile is not the user's own workspace (e.g. stale local state).
  const showEscapeHatch =
    businessProfile?.id &&
    userProfile?.companyId &&
    businessProfile.id !== userProfile.companyId;

  return (
    <>
      <AppNotification />
      <style>{`
        .studio-gradient {
          background: linear-gradient(to bottom, #0F172A, #1E293B);
        }
        .studio-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(99, 102, 241, 0.2);
          background: rgba(30, 41, 59, 0.5);
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .floating {
          animation: float 6s ease-in-out infinite;
        }
        .studio-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }
        .studio-scrollbar::-webkit-scrollbar {
          width: 12px;
        }
        .studio-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .studio-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 6px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .studio-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25);
          background-clip: padding-box;
        }
        /* Group Chat Heroes – main background (app shell handles overflow in style.css) */
        body {
          background: var(--admin-bg-primary);
        }
        #root {
          background: transparent;
        }
        html {
          background: var(--admin-bg-primary);
        }
        
        /* Mobile-specific content area adjustments */
        @media (max-width: 1024px) {
          .flex-1.overflow-y-auto {
            min-height: 0;
            flex: 1;
          }
        }
      `}</style>

      {/* admin-theme: Group Chat Heroes arcade palette (see docs/DESIGN_SYSTEM.md) */}
      <div className="admin-theme gch-layout-bg flex flex-1 min-h-[100vh] min-w-0 safe-area-top" style={studioStyle}>

        {/* Sidebar */}
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {/* Content area: header never shrinks, main scrolls */}
        <div className="relative flex flex-col flex-1 min-h-0 min-w-0 z-10">
          {/* Site header - always visible, never collapsed */}
          <div className="shrink-0">
            <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          </div>

          {/* Maintenance Mode Banner (admin only — non-admins see LockedOutScreen) */}
          {maintenanceMode && isAdmin && (
            <div className="bg-red-600 text-white px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium" style={{ fontFamily: '"Inter", sans-serif' }}>
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
              Maintenance mode is ON. Only admins can access the app.
            </div>
          )}


          {/* Escape hatch: on Engage but viewing a client workspace */}
          {showEscapeHatch && (
            <div className="bg-[var(--admin-cta-solid)]/15 border-b border-[var(--admin-cta-solid)]/30 px-4 py-3 flex items-center justify-center gap-3 flex-wrap" style={{ fontFamily: '"Inter", sans-serif' }}>
              <span className="text-sm font-medium gch-text-primary">
                This workspace is not available on your plan.
              </span>
              <button
                type="button"
                onClick={() => switchActiveClient(null)}
                className="text-sm font-semibold text-[var(--admin-cta-solid)] hover:underline"
              >
                Switch to primary workspace
              </button>
            </div>
          )}

          {/* Main content wrapper - scrolls; header above stays visible */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden studio-scrollbar bg-transparent" data-app-scroll-root>
            <div className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 w-full gch-content-width scroll-content-safe-bottom">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
              >
                <Outlet />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default React.memo(Layout);
